import type { ReactNode } from 'react';
import { AlertTriangle, ChevronLeft, Inbox, RefreshCw } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  back,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Small context line above the title (document type, file format). Keep it short. */
  eyebrow?: ReactNode;
  /** Parent page; replaces the breadcrumb trail on detail pages. */
  back?: { to: string; label: string };
  className?: string;
}) {
  return (
    <div className={cn('pb-4', className)}>
      {back && (
        <Link to={back.to} className="mb-1.5 inline-flex items-center gap-0.5 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="size-3.5" aria-hidden />
          {back.label}
        </Link>
      )}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          {eyebrow && <div className="mb-0.5 text-xs text-muted-foreground">{eyebrow}</div>}
          <h1 className="text-xl font-semibold tracking-tight [overflow-wrap:anywhere]">{title}</h1>
          {description && <div className="mt-0.5 max-w-2xl text-sm text-muted-foreground">{description}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

export function Page({ children, className, narrow }: { children: ReactNode; className?: string; narrow?: boolean }) {
  return <div className={cn('page-gutter py-5', narrow && 'max-w-3xl', className)}>{children}</div>;
}

/** A titled group of content on the page itself (a heading, no box). Use Panel only when a boundary is needed. */
export function Section({
  title,
  description,
  actions,
  children,
  className,
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={cn('py-4 first:pt-0', className)} aria-labelledby={id ? `${id}-title` : undefined}>
      {(title || actions) && (
        <div className="mb-2 flex items-start justify-between gap-3">
          <div>
            {title && (
              <h2 id={id ? `${id}-title` : undefined} className="text-base font-semibold">
                {title}
              </h2>
            )}
            {description && <div className="mt-0.5 text-sm text-muted-foreground">{description}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** Nothing here yet: say what will appear and offer the next step. No illustration, no icon disc. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: typeof Inbox;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div role="status" className={cn('flex flex-col items-center justify-center rounded-lg border border-dashed px-6 py-12 text-center', className)}>
      {Icon && <Icon className="mb-2 size-5 text-muted-foreground" aria-hidden />}
      <h3 className="text-base font-semibold">{title}</h3>
      {description && <div className="mt-1 max-w-md text-sm text-muted-foreground">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, title = 'We couldn’t load this' }: { error: unknown; onRetry?: () => void; title?: string }) {
  const api = error instanceof ApiError ? error : null;
  const forbidden = api?.status === 403;
  return (
    <div role="alert" className="flex flex-col items-center justify-center rounded-lg border border-danger/30 bg-danger-soft px-6 py-10 text-center">
      <AlertTriangle className="mb-2 size-5 text-danger" aria-hidden />
      <h3 className="text-base font-semibold text-danger">{forbidden ? 'You don’t have access to this' : title}</h3>
      <p className="mt-1 max-w-md text-sm text-foreground/80">{(error as Error)?.message ?? 'Something went wrong.'}</p>
      {api?.requestId && <p className="mt-1 text-xs text-muted-foreground">Reference: {api.requestId}</p>}
      {onRetry && !forbidden && (
        <Button variant="secondary" size="sm" className="mt-4" onClick={onRetry}>
          <RefreshCw className="size-4" /> Try again
        </Button>
      )}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card" aria-busy="true" aria-label="Loading">
      <div className="h-9 border-b bg-muted/60" />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex h-10 items-center gap-6 border-b px-3 last:border-0">
          {Array.from({ length: cols }, (_, j) => (
            <Skeleton key={j} className={cn('h-3.5', j === 0 ? 'w-28' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Handles the loading / error / empty states of a query so every screen has all three.
 * `isEmpty` decides what counts as "nothing here yet".
 */
export function DataState<T>({
  query,
  skeleton,
  isEmpty,
  empty,
  children,
}: {
  query: Pick<UseQueryResult<T>, 'data' | 'error' | 'isLoading' | 'isError' | 'refetch'>;
  skeleton?: ReactNode;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return <>{skeleton ?? <TableSkeleton />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.data === undefined) return null;
  if (isEmpty?.(query.data)) return <>{empty}</>;
  return <>{children(query.data)}</>;
}

export function LinkButton({ to, children, ...rest }: { to: string; children: ReactNode } & React.ComponentProps<typeof Button>) {
  return (
    <Button asChild {...rest}>
      <Link to={to}>{children}</Link>
    </Button>
  );
}
